import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc, serverTimestamp, query, where, getDoc, doc } from 'firebase/firestore';
import { db, auth } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { FaTrash, FaGripVertical } from 'react-icons/fa';
import './CreateTasklist.css';

function CreateTasklist() {
  const [listName, setListName] = useState('');
  const [items, setItems] = useState([]);
  const [fontSizes, setFontSizes] = useState({
    section: 18,
    subsection: 16,
    item: 14
  });
  const [isTemplate, setIsTemplate] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    console.log('Auth state changed:', {
      user: user ? 'logged in' : 'not logged in',
      uid: user?.uid
    });
  }, [user]);

  useEffect(() => {
    const checkAuth = async () => {
      console.log('Current auth state:', {
        contextUser: user,
        authCurrentUser: auth.currentUser,
        uid: user?.uid
      });
      
      const timer = setTimeout(() => {
        if (!user?.uid && !auth.currentUser?.uid) {
          console.log('No authenticated user found');
          navigate('/login');
        }
      }, 2000);

      return () => clearTimeout(timer);
    };

    checkAuth();
  }, [user, navigate]);

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const reorderedItems = Array.from(items);
    const [removed] = reorderedItems.splice(result.source.index, 1);
    reorderedItems.splice(result.destination.index, 0, removed);
    setItems(reorderedItems);
  };

  const addItem = (type) => {
    const newItem = {
      id: `item-${Date.now()}`,
      type,
      content: '',
      backgroundColor: type === 'section' 
        ? 'rgba(33, 150, 243, 0.15)' 
        : type === 'subsection'
        ? 'rgba(76, 175, 80, 0.15)'
        : 'rgba(255, 152, 0, 0.15)'
    };
    setItems(prev => [...prev, newItem]);
  };

  const validateTemplateData = () => {
    if (!listName.trim()) {
      alert('Please enter a template name');
      return false;
    }

    if (items.length === 0) {
      alert('Please add at least one item');
      return false;
    }

    // Validate that all items have content
    const emptyItems = items.filter(item => !item.content.trim());
    if (emptyItems.length > 0) {
      alert('Please fill in content for all items');
      return false;
    }

    return true;
  };

  const saveTemplate = async () => {
    if (!validateTemplateData()) return;
    
    const userId = user?.uid || auth.currentUser?.uid;
    setLoading(true);
    
    try {
      const now = new Date();
      const templateData = {
        name: listName.trim(),
        items: items.map(item => ({
          id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          type: item.type,
          content: item.content,
          isCompleted: false,
          backgroundColor: item.backgroundColor || (
            item.type === 'section' 
              ? 'rgba(33, 150, 243, 0.15)' 
              : item.type === 'subsection'
              ? 'rgba(76, 175, 80, 0.15)'
              : 'rgba(255, 152, 0, 0.15)'
          ),
          fontSize: fontSizes[item.type]
        })),
        fontSizes,
        userId,
        createdAt: now,
        lastModified: now,
        isTemplate: true,
        status: 'active',
        templateVersion: 1,
        templateStyles: {
          section: {
            backgroundColor: 'rgba(33, 150, 243, 0.15)',
            fontSize: fontSizes.section
          },
          subsection: {
            backgroundColor: 'rgba(76, 175, 80, 0.15)',
            fontSize: fontSizes.subsection
          },
          item: {
            backgroundColor: 'rgba(255, 152, 0, 0.15)',
            fontSize: fontSizes.item
          }
        }
      };

      const docRef = await addDoc(collection(db, 'templates'), {
        ...templateData,
        createdAt: serverTimestamp(),
        lastModified: serverTimestamp()
      });
      
      const eventData = {
        ...templateData,
        id: docRef.id,
        templateId: docRef.id
      };
      
      // Dispatch event for the Forms component to handle
      window.dispatchEvent(new CustomEvent('templateCreated', {
        detail: eventData
      }));

      // Navigate back to the forms page with create tab active
      navigate('/forms', { 
        state: { 
          activeTab: 'create',
          newTemplateId: docRef.id,
          scrollToTop: true
        }
      });
    } catch (error) {
      console.error('Error saving template:', error);
      handleSaveError(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="create-tasklist-container">
      <h1>Create New Template</h1>
      
      <div className="tasklist-header">
        <div className="form-section">
          <label>Template Name:</label>
          <input
            type="text"
            value={listName}
            onChange={(e) => setListName(e.target.value)}
            placeholder="Enter template name..."
            className="name-input"
          />
        </div>

        <div className="font-size-controls">
          {Object.entries(fontSizes).map(([type, size]) => (
            <div key={type} className="font-size-control">
              <label>{type.charAt(0).toUpperCase() + type.slice(1)} Font Size:</label>
              <input
                type="number"
                min="8"
                max="32"
                value={size}
                onChange={(e) => setFontSizes(prev => ({

                  ...prev,
                  [type]: parseInt(e.target.value) || prev[type]
                }))}

              />
              <span>px</span>
            </div>
          ))}
        </div>
      </div>

      <div className="tasklist-content">
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="tasklist-items">
            {(provided) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
                className="tasklist-items"
              >
                {items.map((item, index) => (
                  <Draggable 
                    key={item.id} 
                    draggableId={item.id} 
                    index={index}
                  >
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        className={`tasklist-item ${item.type} ${snapshot.isDragging ? 'is-dragging' : ''}`}
                      >
                        <div {...provided.dragHandleProps} className="drag-handle">
                          <FaGripVertical />
                        </div>
                        <input
                          type="text"
                          value={item.content}
                          onChange={(e) => {
                            const newValue = e.target.value;
                            setItems(prev => 
                              prev.map(i => 
                                i.id === item.id ? { ...i, content: newValue } : i
                              )
                            );
                          }}
                          onClick={e => e.stopPropagation()}
                          placeholder={`Enter ${item.type} text`}
                          className="tasklist-input"
                          style={{ fontSize: `${fontSizes[item.type]}px` }}
                        />
                        <button
                          onClick={() => setItems(prev => prev.filter(i => i.id !== item.id))}
                          className="delete-btn-template"
                          type="button"
                          aria-label="Delete item"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>

      <div className="add-buttons-container">
        <button 
          onClick={() => addItem('section')} 
          className="add-btn section"
          type="button"
        >
          Checkbox Section
        </button>
        <button 
          onClick={() => addItem('subsection')} 
          className="add-btn subsection"
          type="button"
        >
          Checkbox Subsection
        </button>
        <button 
          onClick={() => addItem('item')} 
          className="add-btn item"
          type="button"
        >
          Checkbox Item
        </button>
      </div>

      <button 
        onClick={saveTemplate} 
        className="save-btn"
        type="button"
      >
        Save Template
      </button>
    </div>
  );
}

export default CreateTasklist;